import {
  Outlet,
  createFileRoute,
  notFound,
  stripSearchParams,
} from '@tanstack/react-router'
import { Suspense } from 'react'

import { Center, Spinner } from '@masterplandev/ui'

import { CompanyPage } from '@/app/manager/CompanyPage/CompanyPage'
import { PageContainer } from '@/app/manager/Layout'
import { NoLicensesToolbar } from '@/app/manager/NoLicensesToolbar/NoLicensesToolbar'
import {
  FEATURE_ENHANCED_DATA_TABLE,
  FEATURE_USER_MANAGEMENT,
} from '@/core/constants/features'
import { buildPageTitle } from '@/core/utils/buildPageTitle/buildPageTitle'
import useFeatures from '@/core/utils/features/useFeatures'
import { usersQueries } from '@/http/api/users'
import { UsersList } from '@/users/components/UsersList/UsersList'
import {
  defaultQueryValues,
  persistUsersListSearchParams,
  usersListQueryParamsSchema,
} from '@/users/utils/usersListSearchParams'

export const Route = createFileRoute('/company/_management/users')({
  head: () => ({
    meta: [
      {
        title: buildPageTitle({
          titleKey: 'page-title-users',
        }),
      },
    ],
  }),
  beforeLoad: ({ cause, context: { user }, search }) => {
    if (!user?.features.includes(FEATURE_USER_MANAGEMENT)) {
      throw notFound()
    }

    persistUsersListSearchParams(cause, search)
  },
  loader: ({ context: { queryClient } }) => {
    queryClient.prefetchQuery(usersQueries.usersList())
  },
  component: RouteComponent,
  validateSearch: usersListQueryParamsSchema,
  search: {
    middlewares: [stripSearchParams(defaultQueryValues)],
  },
})

function RouteComponent() {
  const hasEnhancedDataTableFeature = useFeatures(FEATURE_ENHANCED_DATA_TABLE)
  return (
    <>
      <CompanyPage
        data-testid="company-users-page"
        blockScroll={hasEnhancedDataTableFeature}>
        <NoLicensesToolbar />
        <PageContainer
          py="10"
          flex="1"
          minH="0"
          display="flex"
          flexDirection="column">
          <Suspense
            fallback={
              <Center h="sm">
                <Spinner size="xl" />
              </Center>
            }>
            <UsersList />
          </Suspense>
        </PageContainer>
      </CompanyPage>
      <Outlet />
    </>
  )
}
